import { type FormEvent, type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, Input, Skeleton, Spinner } from '@/components/ui';
import {
  useBranding,
  useUpdateBranding,
  type BrandingInput,
} from '@/hooks/useAdminBranding';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Reseller admin → White-label branding. Lives at /admin/branding, gated
// by RequireReseller. The form on the left edits logo / primary color /
// custom domain / transactional email identity; the right column shows a
// live preview that re-renders as the operator types.
//
// Storage only in this iteration — wiring the customer portal to actually
// apply the reseller's branding (CSS overrides + logo swap + email
// from-name) lands in a follow-up PR.
// ---------------------------------------------------------------------------

const PRESET_COLORS = [
  '#4ADE80',
  '#3B82F6',
  '#7C3AED',
  '#F59E0B',
  '#EF4444',
  '#0EA5E9',
  '#EC4899',
  '#10B981',
];

const VOLT_DEFAULT = '#4ADE80';

interface DraftState {
  logoUrl: string;
  primaryColor: string;
  customDomain: string;
  emailFromName: string;
  emailReplyTo: string;
}

const EMPTY_DRAFT: DraftState = {
  logoUrl: '',
  primaryColor: '',
  customDomain: '',
  emailFromName: '',
  emailReplyTo: '',
};

export default function AdminBrandingPage(): JSX.Element {
  const { t } = useTranslation('default');
  const { user } = useAuth();
  const branding = useBranding();
  const update = useUpdateBranding();
  // Drives the placeholder logo in both the form preview and the mini
  // portal preview so the operator sees a meaningful initial (their
  // workspace's first letter) instead of a fixed "V".
  const initial = user?.initial ?? '?';

  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);

  // Auto-hide the "Saved" badge after a short delay. Without the timer
  // the conditional `Date.now() - savedAt < 4000` only re-evaluates on
  // the next render, leaving the badge stuck if the user idles.
  useEffect(() => {
    if (!showSaved) return;
    const id = window.setTimeout(() => setShowSaved(false), 4_000);
    return () => window.clearTimeout(id);
  }, [showSaved]);

  // Hydrate the draft once the GET resolves. Subsequent invalidations
  // from a successful save also flow through here so the preview stays
  // in lockstep with the canonical server state.
  useEffect(() => {
    if (!branding.data) return;
    setDraft({
      logoUrl: branding.data.logo_url ?? '',
      primaryColor: branding.data.primary_color ?? '',
      customDomain: branding.data.custom_domain ?? '',
      emailFromName: branding.data.email_from_name ?? '',
      emailReplyTo: branding.data.email_reply_to ?? '',
    });
  }, [branding.data]);

  if (branding.isLoading) {
    return <BrandingSkeleton />;
  }

  if (branding.error || !branding.data) {
    return (
      <div className="px-8 py-8 max-w-[1200px] mx-auto">
        <Card padding="lg">
          <div className="text-center py-10">
            <p className="text-[14px] text-text-2 font-medium">
              {t('abr_load_error_title')}
            </p>
            <p className="text-[13px] text-text-3 mt-1">
              {branding.error instanceof ApiError
                ? branding.error.message
                : t('abr_load_error_sub')}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const input: BrandingInput = {
      logo_url: draft.logoUrl.trim(),
      primary_color: draft.primaryColor.trim(),
      custom_domain: draft.customDomain.trim(),
      email_from_name: draft.emailFromName.trim(),
      email_reply_to: draft.emailReplyTo.trim(),
    };
    try {
      await update.mutateAsync(input);
      setShowSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('abr_err_generic'));
    }
  };

  const discard = (): void => {
    if (!branding.data) return;
    setDraft({
      logoUrl: branding.data.logo_url ?? '',
      primaryColor: branding.data.primary_color ?? '',
      customDomain: branding.data.custom_domain ?? '',
      emailFromName: branding.data.email_from_name ?? '',
      emailReplyTo: branding.data.email_reply_to ?? '',
    });
    setError(null);
  };

  const previewColor =
    isValidHex(draft.primaryColor) ? draft.primaryColor : VOLT_DEFAULT;

  return (
    <form onSubmit={(e) => void submit(e)} className="px-8 py-8 max-w-[1200px] mx-auto">
      <div className="text-[12px] text-text-3 mb-2">{t('abr_breadcrumb')}</div>
      <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0 mb-1.5">
        {t('abr_title')}
      </h1>
      <p className="text-[14px] text-text-2 m-0 mb-7 max-w-[640px]">
        {t('abr_sub')}
      </p>

      {error && (
        <div className="mb-4 px-4 py-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6 items-start">
        {/* Left: form */}
        <div className="flex flex-col gap-5">
          <Section title={t('abr_logo_title')} subtitle={t('abr_logo_sub')}>
            <div className="flex gap-4 items-center">
              <LogoPreview url={draft.logoUrl} fallbackInitial={initial} />
              <div className="flex-1">
                <Input
                  type="url"
                  value={draft.logoUrl}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, logoUrl: e.target.value }))
                  }
                  placeholder="https://cdn.acme.com/logo.svg"
                  maxLength={500}
                />
                <p className="text-[11.5px] text-text-4 mt-1.5">
                  {t('abr_logo_hint')}
                </p>
              </div>
            </div>
          </Section>

          <Section title={t('abr_color_title')} subtitle={t('abr_color_sub')}>
            <div className="flex gap-2.5 items-center mb-3">
              <div
                className="w-11 h-9 rounded-sm border border-line flex-shrink-0"
                style={{ background: previewColor }}
              />
              <Input
                value={draft.primaryColor}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, primaryColor: e.target.value }))
                }
                placeholder="#4ADE80"
                maxLength={7}
                className="mono flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-text-3"
                onClick={() => setDraft((p) => ({ ...p, primaryColor: '' }))}
              >
                {t('abr_color_reset')}
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {PRESET_COLORS.map((c) => {
                // Normalize the user's input the same way the server
                // does so a typed "#4ade80" highlights the matching
                // preset before save.
                const selected = normalizeHex(draft.primaryColor) === c;
                return (
                  <button
                    key={c}
                    type="button"
                    aria-label={c}
                    onClick={() => setDraft((p) => ({ ...p, primaryColor: c }))}
                    className={cn(
                      'w-7 h-7 rounded-full border-2 transition-transform hover:scale-110',
                      selected ? 'border-text' : 'border-surface',
                    )}
                    style={{
                      background: c,
                      boxShadow: '0 0 0 1px var(--line)',
                    }}
                  />
                );
              })}
            </div>
          </Section>

          <Section title={t('abr_domain_title')} subtitle={t('abr_domain_sub')}>
            <Input
              value={draft.customDomain}
              onChange={(e) =>
                setDraft((p) => ({
                  ...p,
                  customDomain: e.target.value.toLowerCase(),
                }))
              }
              placeholder="portal.acme.com"
              maxLength={253}
              className="mono mb-3"
            />
            {(branding.data.custom_domain ?? '') !== '' && (
              <DomainVerification
                domain={branding.data.custom_domain ?? ''}
                verified={branding.data.domain_verified}
              />
            )}
          </Section>

          <Section title={t('abr_email_title')} subtitle={t('abr_email_sub')}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FieldLabel label={t('abr_email_from_label')}>
                <Input
                  value={draft.emailFromName}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, emailFromName: e.target.value }))
                  }
                  placeholder="Acme"
                  maxLength={80}
                />
              </FieldLabel>
              <FieldLabel label={t('abr_email_reply_label')}>
                <Input
                  type="email"
                  value={draft.emailReplyTo}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, emailReplyTo: e.target.value }))
                  }
                  placeholder="support@acme.com"
                  maxLength={254}
                  className="mono"
                />
              </FieldLabel>
            </div>
          </Section>

          <div className="flex justify-end gap-2 pt-1">
            {showSaved && (
              <span className="text-[12px] text-success self-center mr-2">
                {t('abr_saved')}
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={discard}
              disabled={update.isPending}
            >
              {t('abr_discard')}
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? (
                <>
                  <Spinner />
                  <span>{t('abr_saving')}</span>
                </>
              ) : (
                t('abr_save')
              )}
            </Button>
          </div>
        </div>

        {/* Right: live preview */}
        <div className="lg:sticky lg:top-4 flex flex-col gap-3">
          <div className="text-[11px] uppercase tracking-[0.06em] text-text-3 font-medium flex items-center gap-2">
            <span
              aria-hidden="true"
              className="w-1.5 h-1.5 rounded-full bg-volt-500 animate-pulse"
            />
            {t('abr_preview_label')}
          </div>
          <PortalPreview
            color={previewColor}
            logoUrl={draft.logoUrl}
            domain={draft.customDomain || 'portal.acme.com'}
            initial={initial}
          />
          <EmailPreview
            color={previewColor}
            fromName={draft.emailFromName || 'EzSender'}
            replyTo={draft.emailReplyTo || 'support@ezsender.app'}
          />
        </div>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Section + field shells — same style as the design package's group cards.
// ---------------------------------------------------------------------------
interface SectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}
function Section({ title, subtitle, children }: SectionProps): JSX.Element {
  return (
    <Card padding="lg">
      <div className="mb-3.5">
        <div className="text-[14px] font-semibold text-text">{title}</div>
        {subtitle && (
          <p className="text-[12.5px] text-text-3 mt-0.5 leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </Card>
  );
}

interface FieldLabelProps {
  label: string;
  children: React.ReactNode;
}
function FieldLabel({ label, children }: FieldLabelProps): JSX.Element {
  return (
    <label className="block">
      <div className="text-[12px] font-medium text-text-2 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Logo preview — renders the actual image when the URL is set, else a
// volt-gradient initial as a placeholder so the form has something to
// look at while empty.
// ---------------------------------------------------------------------------
interface LogoPreviewProps {
  url: string;
  fallbackInitial: string;
}
function LogoPreview({ url, fallbackInitial }: LogoPreviewProps): JSX.Element {
  const [errored, setErrored] = useState(false);
  // Reset errored when URL changes so a fixed URL re-renders the image.
  useEffect(() => {
    setErrored(false);
  }, [url]);

  if (url && !errored) {
    return (
      <div className="w-[88px] h-[88px] rounded-md border border-line bg-surface-2 grid place-items-center overflow-hidden flex-shrink-0">
        <img
          src={url}
          alt="logo preview"
          className="max-w-full max-h-full object-contain"
          onError={() => setErrored(true)}
        />
      </div>
    );
  }
  return (
    <div
      aria-hidden="true"
      className="w-[88px] h-[88px] rounded-md grid place-items-center text-white text-[36px] font-bold flex-shrink-0"
      style={{
        background: 'linear-gradient(135deg, var(--volt-500), var(--volt-700))',
      }}
    >
      {fallbackInitial}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Domain verification block — DNS instructions + verified/pending pill.
// The verified flag is set by a future verification job; for now the
// admin sees "pending" until that lands.
// ---------------------------------------------------------------------------
interface DomainVerificationProps {
  domain: string;
  verified: boolean;
}
function DomainVerification({
  domain,
  verified,
}: DomainVerificationProps): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <div className="border border-line-soft rounded-md bg-surface-2 px-3.5 py-3">
      <div className="flex justify-between items-center mb-2.5">
        <span className="mono text-[11px] text-text-3 uppercase tracking-[0.04em]">
          {t('abr_dns_title')}
        </span>
        {verified ? (
          <Badge variant="success">{t('abr_dns_verified')}</Badge>
        ) : (
          <Badge variant="warn">{t('abr_dns_pending')}</Badge>
        )}
      </div>
      <div className="grid grid-cols-[60px_140px_1fr] gap-2 mono text-[11.5px] leading-7">
        <span className="text-text-4">{t('abr_dns_type')}</span>
        <span className="text-text-4">{t('abr_dns_host')}</span>
        <span className="text-text-4">{t('abr_dns_value')}</span>
        <span className="text-text-2">CNAME</span>
        <span className="text-text-2">{domain.split('.')[0] || 'portal'}</span>
        <span className="text-text-2">cname.ezsender.app</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live preview — mini browser chrome + tiny mock app that recolors as
// the operator changes the primary color, swaps the logo when set, and
// shows the custom domain in the URL bar.
// ---------------------------------------------------------------------------
interface PortalPreviewProps {
  color: string;
  logoUrl: string;
  domain: string;
  initial: string;
}
function PortalPreview({ color, logoUrl, domain, initial }: PortalPreviewProps): JSX.Element {
  return (
    <div
      className="border border-line rounded-lg overflow-hidden bg-surface"
      style={{ boxShadow: '0 4px 20px rgba(8, 14, 12, 0.06)' }}
    >
      {/* mini browser chrome */}
      <div className="px-3 py-2 bg-surface-2 border-b border-line-soft flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#ED6A5E]" />
          <div className="w-2 h-2 rounded-full bg-[#F4BF4F]" />
          <div className="w-2 h-2 rounded-full bg-[#62C554]" />
        </div>
        <div className="mono flex-1 text-[10.5px] text-text-3 px-2 py-0.5 bg-surface border border-line rounded text-center">
          {domain}/dashboard
        </div>
      </div>
      <div className="grid grid-cols-[60px_1fr] h-[320px]">
        {/* mini sidebar */}
        <div className="bg-surface-2 border-r border-line-soft px-2 py-3 flex flex-col gap-1.5">
          <div
            className="w-8 h-8 rounded-md grid place-items-center text-white font-bold text-[14px] overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${color}, ${color})` }}
          >
            {logoUrl ? (
              <img src={logoUrl} alt="" className="max-w-full max-h-full object-contain" />
            ) : (
              initial
            )}
          </div>
          <div className="h-px bg-line-soft my-1" />
          {[true, false, false, false, false].map((active, i) => (
            <div
              key={i}
              className="w-full h-2 rounded-sm"
              style={{
                background: active ? color : 'var(--line)',
                opacity: active ? 1 : 0.6,
              }}
            />
          ))}
        </div>
        <div className="p-4">
          <div className="h-3.5 w-24 bg-text/85 rounded-sm mb-1" />
          <div className="h-2 w-32 bg-text-3/40 rounded-sm mb-3.5" />
          <div className="flex gap-2 mb-3.5">
            <div
              className="px-3 py-1.5 text-[11px] font-medium rounded-md text-white"
              style={{ background: color }}
            >
              + New campaign
            </div>
            <div className="px-3 py-1.5 text-[11px] rounded-md border border-line bg-surface text-text-2">
              Filter
            </div>
          </div>
          <div
            className="rounded-lg p-3 mb-2.5 border-2"
            style={{
              borderColor: color,
              background: `${color}0F`,
            }}
          >
            <div className="flex justify-between items-center mb-2">
              <div className="h-2 w-20 bg-text-2/80 rounded-sm" />
              <div
                className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold text-white"
                style={{ background: color }}
              >
                ACTIVE
              </div>
            </div>
            <div className="h-1.5 bg-line rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: '62%', background: color }} />
            </div>
          </div>
          {[0, 1].map((i) => (
            <div key={i} className="rounded-lg p-3 mb-2 border border-line-soft">
              <div
                className="h-2 bg-text-3/50 rounded-sm"
                style={{ width: `${60 + i * 15}px` }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email preview — mock transactional email so the operator sees how the
// from-name + reply-to + primary color look together.
// ---------------------------------------------------------------------------
interface EmailPreviewProps {
  color: string;
  fromName: string;
  replyTo: string;
}
function EmailPreview({ color, fromName, replyTo }: EmailPreviewProps): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.06em] text-text-3 font-medium mb-2 mt-2">
        {t('abr_email_preview_label')}
      </div>
      <div className="border border-line rounded-md p-4 bg-surface text-[12px]">
        <div className="mono text-[11px] text-text-3 mb-2">
          {t('abr_email_from_field')}
          <span className="text-text">
            {' '}
            {fromName} &lt;{replyTo}&gt;
          </span>
        </div>
        <div className="text-text font-medium mb-1">
          {t('abr_email_subject', { from: fromName })}
        </div>
        <div className="text-text-2 leading-relaxed">{t('abr_email_body')}</div>
        <div
          className="inline-block mt-3 px-3 py-1.5 rounded-md text-white text-[11px] font-medium"
          style={{ background: color }}
        >
          {t('abr_email_cta')}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------
function BrandingSkeleton(): JSX.Element {
  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <Skeleton className="h-3 w-32 mb-3" />
      <Skeleton className="h-7 w-40 mb-2" />
      <Skeleton className="h-4 w-96 mb-7" />
      <div className="grid grid-cols-[1.1fr_1fr] gap-6">
        <div className="space-y-5">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-[500px]" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isValidHex(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value.trim());
}

// normalizeHex mirrors the server-side normalization (expand short form,
// uppercase) so client-side preset comparisons match before a save.
// Returns the input unchanged when it doesn't look like a hex color.
function normalizeHex(value: string): string {
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
    const r = trimmed[1];
    const g = trimmed[2];
    const b = trimmed[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return trimmed;
}
